/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MATTER_KIND_LABEL } from '@kd/shared';
import { DigestWidget } from './Agent';
import { Button, ErrorText, Field, Input, PageHeader, Spinner } from '@/components/ui';
import { api, download, qs } from '@/lib/api';
import { fmtDate, fmtDateTime, fmtMoney, today } from '@/lib/format';
import clsx from 'clsx';
import { EventsList } from '@/pages/Monitoring';

function Kpi({ label, value, tone, money }: { label: string; value: string | number; tone?: 'red'; money?: boolean }) {
  return (
    <div className="card p-4">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`mt-1 whitespace-nowrap font-semibold tabular-nums ${money ? 'text-lg' : 'text-2xl'} ${tone === 'red' && Number(value) > 0 ? 'text-red-600' : ''}`}>{value}</div>
    </div>
  );
}

export function DashboardPage() {
  const { hasModule } = useAuth();
  const [from, setFrom] = useState(`${new Date().getFullYear()}-01-01`);
  const [to, setTo] = useState(today());
  const { data, isLoading } = useQuery({ queryKey: ['dashboard', from, to], queryFn: () => api.get(`/reports/dashboard${qs({ from, to })}`) });
  if (isLoading || !data) return <Spinner />;
  const k = data.kpi;
  const maxRev = Math.max(1, ...data.revenue.map((r: any) => Number(r.received)));
  return (
    <>
      <DemoBanner />
      {hasModule('M32') && <div className="mb-5"><DigestWidget /></div>}
      <PageHeader title="Сводка" subtitle={`Период: ${fmtDate(data.period.from)} - ${fmtDate(data.period.to)}`}
        actions={<>
          <Field label="С"><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
          <Field label="По"><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
          <div className="self-end"><Button variant="secondary" onClick={() => download('/reports/export/profitability')}>Рентабельность в Excel</Button></div>
        </>} />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-[repeat(5,minmax(0,1fr))_repeat(2,minmax(0,1.4fr))]">
        <Kpi label="Клиенты" value={k.clients} />
        <Kpi label="Открытые сделки" value={k.open_deals} />
        <Kpi label="Действующие договоры" value={k.active_contracts} />
        <Kpi label="Дела в работе" value={k.open_matters} />
        <Kpi label="Просроченные задачи" value={k.overdue_tasks} tone="red" />
        <Kpi label="Поступило" value={fmtMoney(k.received)} money />
        <Kpi label="Затраты" value={fmtMoney(k.spent)} money />
      </div>

      <LegalWidgets />
      <div className="mt-5 grid gap-5 xl:grid-cols-2">
        <section className="card min-w-0 p-4">
          <h2 className="mb-3 text-sm font-semibold">Поступления по месяцам</h2>
          {data.revenue.length === 0 ? <div className="text-sm text-slate-500">Платежей за период нет</div> : (
            <div className="space-y-1.5">
              {data.revenue.map((r: any) => (
                <div key={r.month} className="flex items-center gap-3 text-sm">
                  <span className="w-16 text-slate-500">{r.month}</span>
                  <div className="h-4 flex-1 rounded bg-slate-100"><div className="h-4 rounded bg-brand-500" style={{ width: `${(Number(r.received) / maxRev) * 100}%` }} /></div>
                  <span className="w-36 text-right tabular-nums">{fmtMoney(r.received)}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="card min-w-0 p-4">
          <h2 className="mb-3 text-sm font-semibold">Дебиторская задолженность клиентов</h2>
          {data.debtors.length === 0 ? <div className="text-sm text-slate-500">Задолженности нет</div> : (
            <table className="w-full">
              <thead><tr><th className="th">Договор</th><th className="th">Клиент</th><th className="th text-right">Долг</th></tr></thead>
              <tbody>
                {data.debtors.slice(0, 10).map((d: any) => (
                  <tr key={d.id} className="border-t border-slate-100">
                    <td className="td"><Link className="text-brand-700 hover:underline" to={`/contracts/${d.id}`}>№ {d.number}</Link></td>
                    <td className="td">{d.party_name}</td>
                    <td className="td text-right tabular-nums">{fmtMoney(d.debt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="card min-w-0 p-4">
          <h2 className="mb-3 text-sm font-semibold">Загрузка сотрудников</h2>
          <table className="w-full">
            <thead><tr><th className="th">Сотрудник</th><th className="th text-right">В работе</th><th className="th text-right">Просрочено</th><th className="th text-right">Выполнено</th></tr></thead>
            <tbody>
              {data.workload.map((w: any) => (
                <tr key={w.id} className="border-t border-slate-100">
                  <td className="td">{w.full_name}</td>
                  <td className="td text-right tabular-nums">{w.open}</td>
                  <td className={`td text-right tabular-nums ${w.overdue ? 'text-red-600' : ''}`}>{w.overdue}</td>
                  <td className="td text-right tabular-nums">{w.done}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="card min-w-0 p-4">
          <h2 className="mb-3 text-sm font-semibold">Дела по видам</h2>
          {data.matters.length === 0 ? <div className="text-sm text-slate-500">Дел пока нет</div> : (
            <table className="w-full">
              <tbody>
                {data.matters.map((m: any) => (
                  <tr key={m.kind + m.status} className="border-t border-slate-100">
                    <td className="td">{MATTER_KIND_LABEL[m.kind] ?? m.kind}</td>
                    <td className="td text-slate-500">{m.status === 'OPEN' ? 'В работе' : m.status === 'CLOSED' ? 'Закрыто' : m.status}</td>
                    <td className="td text-right tabular-nums">{m.n}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </>
  );
}

/** Предложение заполнить пустую базу демонстрационными данными (только администратору) */
function DemoBanner() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const { data } = useQuery({ enabled: can('user.manage'), queryKey: ['demo-status'], queryFn: () => api.get('/admin/demo-data') });
  const gen = useMutation({ mutationFn: () => api.post('/admin/demo-data'), onSuccess: () => qc.invalidateQueries() });
  if (!can('user.manage') || !data || data.loaded) return null;
  return (
    <div className="card mb-4 flex flex-wrap items-center justify-between gap-3 border-brand-200 bg-brand-50 p-4">
      <div className="text-sm"><b>Посмотреть программу на примерах?</b> Будут созданы вымышленные клиенты, дела, процедуры банкротства, реестры, торги, долги, задачи и документы со всеми связями.</div>
      <div className="flex items-center gap-2"><ErrorText error={gen.error} /><Button disabled={gen.isPending} onClick={() => gen.mutate()}>{gen.isPending ? 'Создаю...' : 'Заполнить демо-данными'}</Button></div>
    </div>
  );
}

function LegalWidgets() {
  const { hasModule } = useAuth();
  const m12 = hasModule('M12'); const m41 = hasModule('M41');
  const { data: hs } = useQuery({ queryKey: ['hearings'], queryFn: () => api.get('/court-cases/hearings'), enabled: m12 });
  if (!m12 && !m41) return null;
  return (
    <div className="mt-5 grid gap-5 xl:grid-cols-2">
      {m12 && <section className="card min-w-0">
        <div className="flex items-center justify-between border-b border-slate-200 p-3"><h2 className="text-sm font-semibold">Ближайшие заседания</h2><Link to="/court-cases" className="text-xs text-brand-700 hover:underline">все дела</Link></div>
        {!hs?.length ? <div className="p-4 text-sm text-slate-500">Заседаний не назначено</div> : (
          <ul className="divide-y divide-slate-100">{hs.slice(0, 8).map((h: any) => (
            <li key={h.id} className="flex gap-3 p-2.5 text-sm">
              <span className={clsx('w-32 shrink-0 tabular-nums', new Date(h.at).getTime() - Date.now() < 2 * 86400_000 && 'font-semibold text-red-600')}>{fmtDateTime(h.at)}</span>
              <Link to={`/court-cases/${h.id}`} className="font-medium text-brand-700 hover:underline">{h.number}</Link>
              <span className="min-w-0 truncate text-slate-500">{[h.partyName, h.court, h.place].filter(Boolean).join(' · ')}</span>
            </li>
          ))}</ul>
        )}
      </section>}
      {m41 && <section className="card min-w-0">
        <div className="flex items-center justify-between border-b border-slate-200 p-3"><h2 className="text-sm font-semibold">Мониторинг: новые события</h2><Link to="/monitoring" className="text-xs text-brand-700 hover:underline">все события</Link></div>
        <EventsList unseenOnly compact />
      </section>}
    </div>
  );
}
